/**
 * Phase 8: shown instead of <SignIn/> when this browser is offline (or the
 * server is unreachable) AND has already completed OfflineSetup.tsx's
 * enrolment - see App.tsx's `status === 'anonymous'` branch for the actual
 * gate. Mirrors DeviceSetup.tsx's shape (a full-screen, setup/unlock-style
 * blocking component) rather than SignIn's marketing-page layout, since
 * there is nothing to sell somebody who is already mid-shift on this till.
 *
 * Everything here runs against the local OPFS database with no network
 * call: resume the file, find the one enrolled local user
 * (`LocalDatabase.auth.enrolledUser()`), verify their PIN
 * (`LocalDatabase.auth.unlockOffline()`), and hand the resulting session
 * straight into `AuthProvider` via `signInOffline()`. A browser that has
 * never run OfflineSetup (no local file, or a file with nobody enrolled)
 * falls back to the ordinary online sign-in form via `onUseOnlineSignIn`.
 */
import { useEffect, useRef, useState, type FormEvent } from 'react';
import type { Permission, Role } from '@kortex/shared';
import {
  LocalDataError,
  OfflineUnlockError,
  type EnrolledLocalUser,
  type LocalDatabase,
  type OfflineUnlockFailureReason,
} from '@kortex/pos-local';
import { useAuth, type Session } from '../lib/auth';
import { resumeLocalDatabaseForUnlock } from '../lib/posData';
import { TextInput } from '../components/ui';

type Phase = 'loading' | 'no-local-user' | 'ready' | 'error';

const FAILURE_MESSAGE: Record<OfflineUnlockFailureReason, string> = {
  NOT_ENROLLED: 'This account has not set up offline access on this browser.',
  LOCKED_OUT: 'Too many wrong PINs. Wait a bit and try again.',
  WRONG_PIN: 'That PIN is not right.',
  GRANT_INVALID: "This browser's offline access could not be verified. Sign in online once to fix it.",
  GRANT_EXPIRED: 'Offline access on this browser has expired. Sign in online once to renew it.',
  DEVICE_LOCKED:
    'This device is locked: it has not reached the server for too long, or its clock is wrong. Connect it to the internet (and check the date and time) to unlock it. Bills on it are kept.',
};

export function OfflineUnlock({ onUseOnlineSignIn }: { onUseOnlineSignIn: () => void }) {
  const { signInOffline } = useAuth();
  const [phase, setPhase] = useState<Phase>('loading');
  const [enrolled, setEnrolled] = useState<EnrolledLocalUser | null>(null);
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Kept across renders rather than reopened per keystroke: OPFS's
  // SyncAccessHandle is exclusive to one open handle at a time (opfs.ts's
  // file doc), so this component holds the one handle for its whole
  // lifetime and releases it exactly once, on success or unmount.
  const dbRef = useRef<LocalDatabase | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      let db: LocalDatabase;
      try {
        db = await resumeLocalDatabaseForUnlock();
      } catch {
        if (!cancelled) setPhase('no-local-user');
        return;
      }
      if (cancelled) {
        void db.close();
        return;
      }
      try {
        const user = await db.auth.enrolledUser();
        if (!user) {
          await db.close();
          setPhase('no-local-user');
          return;
        }
        dbRef.current = db;
        setEnrolled(user);
        setPhase('ready');
      } catch {
        await db.close();
        setPhase('error');
      }
    })();
    return () => {
      cancelled = true;
      const db = dbRef.current;
      dbRef.current = null;
      if (db) void db.close();
    };
  }, []);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const db = dbRef.current;
    if (!db || !enrolled) return;
    setBusy(true);
    setError(null);
    try {
      const result = await db.auth.unlockOffline(enrolled.userId, pin);
      const offlineSession: Session = {
        user: { id: enrolled.userId, name: enrolled.name, email: enrolled.email },
        property: {
          id: enrolled.propertyId,
          name: enrolled.propertyName,
          gstin: enrolled.gstin,
          stateCode: enrolled.stateCode,
        },
        role: (result.role || null) as Role | null,
        permissions: result.permissions as Permission[],
        platformRole: 'NONE',
        // Nothing to switch to offline - a locally-enrolled device is
        // always pinned to the one property its database was provisioned
        // for (EnrolledLocalUser's own doc comment).
        properties: [],
      };
      // Release this component's handle before handing control back to the
      // app shell: posData/index.ts's own resume() call (triggered by the
      // session now being 'authenticated') needs to reopen the same file.
      dbRef.current = null;
      await db.close();
      signInOffline(offlineSession);
    } catch (caught) {
      if (caught instanceof OfflineUnlockError) {
        setError(FAILURE_MESSAGE[caught.reason]);
      } else if (caught instanceof LocalDataError) {
        setError(caught.message);
      } else {
        setError('Could not sign in offline. Try again.');
      }
      setPin('');
    } finally {
      setBusy(false);
    }
  };

  if (phase === 'loading') {
    return (
      <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center' }}>
        <div className="small muted">Opening this device's offline data…</div>
      </div>
    );
  }

  if (phase === 'no-local-user' || phase === 'error') {
    return (
      <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', padding: 24 }}>
        <div className="card" style={{ maxWidth: 420, width: '100%' }}>
          <div className="card-head">
            <h2>Offline access is not set up here</h2>
          </div>
          <div className="card-body stack">
            <p className="small muted">
              {phase === 'error'
                ? "This browser's offline data could not be opened."
                : "Nobody has set up offline access on this browser yet."}{' '}
              Connect to the internet and sign in normally - you can turn on offline access from
              your account menu once you are signed in.
            </p>
            <button className="btn primary" onClick={onUseOnlineSignIn}>
              Go to sign in
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', padding: 24 }}>
      <div className="card" style={{ maxWidth: 420, width: '100%' }}>
        <div className="card-head">
          <h2>You're offline</h2>
        </div>
        <div className="card-body stack">
          <p className="small muted">
            Sign in with your PIN to keep working. Signing in this way only unlocks{' '}
            <b>{enrolled?.name}</b>'s account on this device, for this shift.
          </p>
          <form onSubmit={(e) => void submit(e)} className="stack">
            {error ? <div className="form-error">{error}</div> : null}
            <TextInput
              label="PIN"
              type="password"
              inputMode="numeric"
              autoComplete="off"
              autoFocus
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 8))}
            />
            <button className="btn primary" type="submit" disabled={busy || pin.length < 4}>
              {busy ? 'Checking…' : 'Unlock'}
            </button>
          </form>
          <button className="btn ghost" onClick={onUseOnlineSignIn}>
            Use online sign-in instead
          </button>
        </div>
      </div>
    </div>
  );
}
